import { deepmerge } from '../deepmerge';
import { getPathValue } from '../get-path-value';

interface ConfigHeaders {
  all?: Record<string, string>;
  [key: string]: Record<string, string> | undefined;
}

interface ConfigPublic {
  default: ConfigRoot;
  [key: string]: ConfigRoot;
}

interface ConfigRoot {
  headers?: ConfigHeaders;
  [key: string]: any;
}

interface Config {
  public: ConfigPublic;
  [key: string]: any;
}

// Private state
let config: Config | null = null;
let rootPath: string | null = null;
let rootConfig: ConfigRoot | null = null;

/**
 * Reset the config state
 */
function resetConfig() {
  config = null;
  rootPath = null;
  rootConfig = null;
}

/**
 * Get root path
 * @param {Object} [configObj=config] - The config object.
 * @returns {string} - The root path.
 */
function getRootPath(configObj: Config | null = config): string {
  if (!configObj) {
    console.warn('No config found. Please call initializeConfig() first.');
    return '/';
  }

  const value = Object.keys(configObj?.public)
    // Sort by number of non-empty segments to find the deepest path
    .sort((a, b) => {
      const aSegments = a.split('/').filter(Boolean).length;
      const bSegments = b.split('/').filter(Boolean).length;
      return bSegments - aSegments;
    })
    .find(
      (key) =>
        window.location.pathname === key ||
        window.location.pathname.startsWith(key)
    );

  return value ?? '/';
}

/**
 * Get list of root paths from public config
 * @returns {Array} - The list of root paths.
 */
function getListOfRootPaths(): string[] {
  if (!config) {
    console.warn('No config found. Please call initializeConfig() first.');
    return [];
  }

  return Object.keys(config.public).filter((root) => root !== 'default');
}

/**
 * Checks if the public config contains more than "default"
 * @returns {boolean} - true if public config contains more than "default"
 */
function isMultistore(): boolean {
  return getListOfRootPaths().length >= 1;
}

/**
 * Retrieves headers from config entries like commerce.headers.pdp.my-header, etc and
 * returns as object of all headers like { my-header: value, ... }
 * @param {string} scope - The scope of the headers to retrieve.
 * @returns {Object} - The headers.
 */
function getHeaders(scope: string): Record<string, string> {
  if (!rootConfig) {
    throw new Error(
      'Configuration not initialized. Call initializeConfig() first.'
    );
  }
  const headers = rootConfig.headers ?? {};
  return {
    ...(headers.all ?? {}),
    ...(headers[scope] ?? {}),
  };
}

/**
 * Applies config overrides from metadata.
 *
 * @param {Object} [configObj] - The base config.
 * @param {string} [root] - The root path.
 * @returns {Object} - The config with overrides applied.
 */
function applyConfigOverrides(
  configObj: Config | null,
  root: string | null
): ConfigRoot {
  const defaultConfig = configObj!.public?.default;

  if (root === '/' || !configObj!.public[root as keyof ConfigPublic])
    return defaultConfig;

  return deepmerge(
    defaultConfig,
    configObj!.public[root as keyof ConfigPublic]
  );
}

/**
 * Initializes the configuration system.
 * @returns {Object} The initialized root configuration
 */
function initializeConfig(configObj: Config): ConfigRoot {
  config = configObj;
  rootPath = getRootPath(config);
  rootConfig = applyConfigOverrides(config, rootPath);
  return rootConfig;
}

/**
 * Retrieves a configuration value.
 *
 * @param {string} configParam - The configuration parameter to retrieve.
 * @returns {string|undefined} - The value of the configuration parameter, or undefined.
 */
function getConfigValue(configParam: string): any {
  if (!rootConfig) {
    throw new Error(
      'Configuration not initialized. Call initializeConfig() first.'
    );
  }
  return getPathValue(rootConfig, configParam);
}

export {
  initializeConfig,
  getRootPath,
  getListOfRootPaths,
  isMultistore,
  getConfigValue,
  getHeaders,
  resetConfig,
};
